import { createFileRoute } from '@tanstack/react-router'
import { usePreferences } from '../lib/preferences'
import { useCurrentUser, isAdminRole, isSuperAdminRole } from '../lib/auth'
import { NAV_ITEMS, navLabel } from '../lib/nav'
import { useFeatureFlags } from '../lib/features'
import { Icon, NAV_ICON } from '../components/layout/Icon'
import { useMessages } from '@/i18n/messages'
import { settingsMessages } from '@/i18n/text/settings'
import { LOCALES, LOCALE_NAMES, useLocale } from '@/i18n/locale'

export const Route = createFileRoute('/settings')({
  component: SettingsPage,
})

function SettingsPage() {
  const { data: user } = useCurrentUser()
  const m = useMessages(settingsMessages)
  const locale = useLocale()
  const { sidebar, setHidden, setCollapsed, resetSidebar, theme, setTheme, setLanguage } = usePreferences()

  const flags = useFeatureFlags()

  const allowed = NAV_ITEMS.filter(
    (i) =>
      (!i.adminOnly || isAdminRole(user))
      && (!i.superAdminOnly || isSuperAdminRole(user))
      && (!i.featureFlag || flags[i.featureFlag])
  )

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-10 py-8 igf-fade flex flex-col gap-6">
      <div>
        <h1 className="font-grotesk font-bold text-[30px] leading-tight text-[var(--fg)] tracking-tight">
          {m.title}
        </h1>
        <p className="mt-2 text-[15px] font-medium text-[var(--muted)]">
          {m.intro}
        </p>
      </div>

      <section className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] shadow-[var(--elev)] p-5">
        <h2 className="font-grotesk font-bold text-base text-[var(--fg)]">{m.language}</h2>
        <p className="mt-0.5 text-xs font-medium text-[var(--muted)]">{m.languageHelp}</p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {LOCALES.map((l) => (
            <button
              key={l}
              type="button"
              lang={l}
              aria-pressed={l === locale}
              onClick={() => setLanguage(l)}
              className="flex items-center gap-3 rounded-xl border px-4 py-3 text-sm font-semibold transition"
              style={{
                borderColor: l === locale ? 'var(--brand)' : 'var(--border)',
                background: l === locale ? 'var(--brand-soft)' : 'transparent',
                color: l === locale ? 'var(--brand)' : 'var(--fg-soft)',
              }}
            >
              <span className="font-grotesk text-xs font-bold">{l.toUpperCase()}</span>
              {LOCALE_NAMES[l]}
            </button>
          ))}
        </div>
      </section>

      {/* Theme */}
      <section className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] shadow-[var(--elev)] p-5">
        <h2 className="font-grotesk font-bold text-base text-[var(--fg)]">{m.theme}</h2>
        <p className="mt-0.5 text-xs font-medium text-[var(--muted)]">{m.themeHelp}</p>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <ThemeOption
            active={theme === 'light'}
            icon="sun"
            label={m.light}
            onClick={() => setTheme('light')}
          />
          <ThemeOption
            active={theme === 'dark'}
            icon="moon"
            label={m.dark}
            onClick={() => setTheme('dark')}
          />
        </div>
      </section>

      {/* Menu */}
      <section className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] shadow-[var(--elev)] overflow-hidden">
        <div className="flex items-center justify-between border-b border-[var(--border)] px-5 py-4">
          <div>
            <h2 className="font-grotesk font-bold text-base text-[var(--fg)]">{m.menu}</h2>
            <p className="mt-0.5 text-xs font-medium text-[var(--muted)]">
              {m.menuHelp}
            </p>
          </div>
          <button
            onClick={resetSidebar}
            className="shrink-0 rounded-[10px] border border-[var(--border)] px-3 py-1.5 text-xs font-semibold text-[var(--fg-soft)] hover:bg-[var(--surface-2)]"
          >
            {m.reset}
          </button>
        </div>

        <ul>
          {allowed.map((item) => {
            const isOn = !sidebar.hidden.includes(item.id)
            const isDashboard = item.id === 'dashboard'
            return (
              <li
                key={item.id}
                className="flex items-center justify-between border-b border-[var(--border)] last:border-0 px-5 py-3"
              >
                <span className="flex items-center gap-3 text-sm font-medium text-[var(--fg-soft)]">
                  <Icon name={NAV_ICON[item.id]} className="w-5 h-5 text-[var(--muted)]" />
                  {navLabel(item.id)}
                  {isDashboard && <span className="text-xs text-[var(--faint)]">{m.alwaysVisible}</span>}
                </span>
                <Toggle checked={isOn} disabled={isDashboard} onChange={(v) => setHidden(item.id, !v)} />
              </li>
            )
          })}
        </ul>

        <div className="flex items-center justify-between border-t border-[var(--border)] px-5 py-4">
          <div>
            <h3 className="text-sm font-semibold text-[var(--fg)]">{m.collapse}</h3>
            <p className="mt-0.5 text-xs font-medium text-[var(--muted)]">
              {m.collapseHelp}
            </p>
          </div>
          <Toggle checked={sidebar.collapsed} onChange={setCollapsed} />
        </div>
      </section>
    </div>
  )
}

function ThemeOption({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean
  icon: 'sun' | 'moon'
  label: string
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 rounded-xl border px-4 py-3 text-sm font-semibold transition"
      style={{
        borderColor: active ? 'var(--brand)' : 'var(--border)',
        background: active ? 'var(--brand-soft)' : 'transparent',
        color: active ? 'var(--brand)' : 'var(--fg-soft)',
      }}
    >
      <Icon name={icon} className="w-5 h-5" />
      {label}
    </button>
  )
}

function Toggle({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean
  disabled?: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
        disabled ? 'opacity-40 cursor-not-allowed' : ''
      }`}
      style={{ background: checked ? 'var(--brand)' : 'var(--border-strong)' }}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  )
}
